import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { GraduationCap, Trash2 } from "lucide-react";

import { lifecycleService, type GraduationCriteria, type StudentGraduation } from "../../services/lifecycleService";
import type { Class, Student } from "../../services/studentService";
import { apiErrorMessage } from "../../utils/apiError";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel } from "../inventory/InventoryParts";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { Blockers, CertificateButton, Checks, LearnerHistory } from "./LifecycleParts";
import { day, graduationBlockers, graduationChecks, graduationConfig, graduationStatus, pct } from "./lifecycleLogic";

/** Graduation proposals for the final class, then the certificate once approved. */
export function GraduationsPanel({ rows, criteria, learners, classes, canManage, isLoading, isError, onRetry, onChanged, initialView, creating, onCreate, onCloseCreate }: {
  rows?: StudentGraduation[];
  criteria: GraduationCriteria[];
  learners: Student[];
  classes: Class[];
  canManage: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  initialView?: string;
  creating: boolean;
  onCreate: () => void;
  onCloseCreate: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const done = (text: string) => { setViewing(null); setNotice({ tone: "success", text }); onChanged(); };
  const criteriaOf = (row: StudentGraduation) => criteria.find((item) => item.id === row.criteria);
  const approve = useMutation({
    mutationFn: (row: StudentGraduation) => lifecycleService.approveGraduation(row.id),
    onSuccess: (_, row) => done(`${row.student_name} has graduated; the certificate is ready.`),
  });
  const reject = useMutation({
    mutationFn: ({ row, reason }: { row: StudentGraduation; reason: string }) => lifecycleService.rejectGraduation(row.id, reason),
    onSuccess: (_, { row }) => done(`${row.student_name}'s graduation proposal was rejected.`),
  });
  const remove = useMutation({
    mutationFn: (row: StudentGraduation) => lifecycleService.deleteStudentGraduation(row.id),
    onSuccess: (_, row) => done(`The graduation proposal for ${row.student_name} was deleted.`),
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "The proposal could not be deleted.") }),
  });

  const columns: RegisterColumn<StudentGraduation>[] = [
    { header: "Learner", cell: (row) => <span><span className="font-medium text-gray-900">{row.student_name}</span> <span className="text-xs text-gray-500">{row.student_id}</span></span>, exportValue: (row) => row.student_name },
    { header: "Student ID", cell: (row) => row.student_id, exportValue: (row) => row.student_id, exportOnly: true },
    { header: "Class", cell: (row) => row.class_level_name, exportValue: (row) => row.class_level_name },
    { header: "Attendance", cell: (row) => pct(row.attendance_percentage), exportValue: (row) => Number(row.attendance_percentage), align: "right" },
    { header: "Average", cell: (row) => pct(row.average_grade), exportValue: (row) => Number(row.average_grade), align: "right" },
    { header: "Criteria", cell: (row) => (row.meets_criteria ? <span className="text-emerald-700">Meets</span> : <span className="text-red-700">Falls short</span>), exportValue: (row) => (row.meets_criteria ? "Meets" : "Falls short") },
    { header: "Graduation", cell: (row) => day(row.graduation_date) || "—", exportValue: (row) => day(row.graduation_date) },
    { header: "Honours", cell: (row) => row.honors || "—", exportValue: (row) => row.honors, exportOnly: true },
    { header: "Certificate", cell: (row) => row.certificate_number || "—", exportValue: (row) => row.certificate_number, exportOnly: true },
    { header: "Status", cell: (row) => <StatusDot {...graduationStatus(row.status)} />, exportValue: (row) => graduationStatus(row.status).label },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  const rowCriteria = row && criteriaOf(row);
  const blockers = row ? graduationBlockers(row, rowCriteria) : [];

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        title="Graduations"
        description="Learners in the final class proposed to graduate, checked against the graduation criteria. Approving graduates the learner, releases their class place and issues the certificate."
        actions={canManage && <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={onCreate}><GraduationCap className="h-4 w-4" /> Propose a graduate</button>}
        rows={rows}
        config={graduationConfig}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={(item) => (canManage && item.status === "eligible" ? "Decide" : "Open")}
        noun={["graduation", "graduations"]}
        exportName={{ file: "graduations", title: "Graduations" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No graduations have been proposed."
      />

      {row && (
        <DetailsModal
          title={row.student_name}
          headline={{ label: "Graduation", value: row.class_level_name, note: `${row.academic_year_name}${row.graduation_date ? ` · ${day(row.graduation_date)}` : ""}` }}
          status={graduationStatus(row.status)}
          facts={[
            ["Student ID", row.student_id],
            ["Criteria", rowCriteria?.name ?? "—"],
            ["Ceremony", day(row.ceremony_date) || "—"],
            ["Honours", row.honors || "—"],
            ["Certificate no.", row.certificate_number || "—"],
            ["Decided by", row.approved_by_name || "—"],
          ]}
          sections={[{ title: "Notes", body: row.notes, tone: row.status === "rejected" ? "danger" : "plain" }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {canManage && (row.status === "eligible" || row.status === "rejected") && (
                <button type="button" disabled={remove.isPending} onClick={() => window.confirm(`Delete the graduation proposal for ${row.student_name}?`) && remove.mutate(row)} className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
              {row.status === "graduated" && row.certificate_issued && <CertificateButton label="Graduation certificate" download={() => lifecycleService.downloadGraduationCertificate(row.id)} />}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && row.status === "eligible" && (
                <button type="button" className="btn btn-primary gap-1.5" disabled={blockers.length > 0 || approve.isPending} onClick={() => window.confirm(`Graduate ${row.student_name}? They leave ${row.class_level_name} and the certificate is issued.`) && approve.mutate(row)}>
                  <GraduationCap className="h-4 w-4" /> {approve.isPending ? "Graduating…" : "Graduate"}
                </button>
              )}
            </>
          }
        >
          <Checks checks={graduationChecks(row, rowCriteria)} />
          {canManage && <Blockers items={blockers} />}
          {approve.isError && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{apiErrorMessage(approve.error, "The graduation could not be approved.")}</p>}
          {canManage && row.status === "eligible" && (
            <ReasonPanel title="Reject the proposal" description="The learner stays in their class. The reason is kept on their record." placeholder="Why the learner is not graduating" context={{ key: "students.lifecycle_note", params: { kind: "graduation_reject", id: row.id } }} button="Reject proposal" danger busy={reject.isPending} error={reject.error} errorText="The proposal could not be rejected." onSubmit={(reason) => reject.mutate({ row, reason })} />
          )}
          <LearnerHistory studentId={row.student} />
        </DetailsModal>
      )}

      {creating && <GraduationForm criteria={criteria.filter((item) => item.is_active)} learners={learners} classes={classes} onClose={onCloseCreate} onDone={(text) => { onCloseCreate(); done(text); }} />}
    </div>
  );
}

function GraduationForm({ criteria, learners, classes, onClose, onDone }: { criteria: GraduationCriteria[]; learners: Student[]; classes: Class[]; onClose: () => void; onDone: (text: string) => void }) {
  const [criteriaId, setCriteriaId] = useState(criteria.length === 1 ? String(criteria[0].id) : "");
  const [student, setStudent] = useState("");
  const [attendance, setAttendance] = useState("");
  const [average, setAverage] = useState("");
  const [hours, setHours] = useState("0");
  const [clubs, setClubs] = useState(false);
  const [passed, setPassed] = useState(false);
  const [honours, setHonours] = useState("");
  const [graduationDate, setGraduationDate] = useState("");
  const [ceremony, setCeremony] = useState("");
  const [notes, setNotes] = useState("");
  const chosen = criteria.find((item) => String(item.id) === criteriaId);
  const graduatingClass = classes.find((item) => item.id === chosen?.class_level);
  const inClass = learners.filter((item) => item.status === "active" && item.current_class === chosen?.class_level);
  const measures = useQuery({ queryKey: ["lifecycle", "measures", student], queryFn: () => lifecycleService.getStudentMeasures(Number(student), graduatingClass?.academic_year), enabled: Boolean(student) });
  useEffect(() => {
    if (!measures.data) return;
    setAttendance(String(Number(measures.data.attendance_percentage)));
    setAverage(String(Number(measures.data.average_grade)));
  }, [measures.data]);
  const checks = chosen && attendance !== "" ? graduationChecks({ attendance_percentage: Number(attendance), average_grade: Number(average), community_service_hours: Number(hours), extracurricular_participation: clubs, final_exam_passed: passed }, chosen) : [];
  const create = useMutation({
    mutationFn: () => lifecycleService.createStudentGraduation({
      student: Number(student),
      class_level: chosen!.class_level,
      academic_year: graduatingClass!.academic_year,
      criteria: chosen!.id,
      attendance_percentage: Number(attendance),
      average_grade: Number(average),
      community_service_hours: Number(hours),
      extracurricular_participation: clubs,
      final_exam_passed: passed,
      honors: honours.trim(),
      graduation_date: graduationDate || undefined,
      ceremony_date: ceremony || undefined,
      notes: notes.trim(),
    }),
    onSuccess: (saved) => onDone(`${saved.student_name} proposed for graduation${saved.meets_criteria ? "" : "; they do not meet the criteria yet"}.`),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    create.mutate();
  };
  const fromRecords = measures.data && Number(attendance) === Number(measures.data.attendance_percentage) && Number(average) === Number(measures.data.average_grade);
  return (
    <FormModal
      id="graduation-form"
      size="3xl"
      title="Propose a graduate"
      busy={create.isPending}
      error={create.isError ? apiErrorMessage(create.error, "The proposal could not be made.") : undefined}
      saveLabel="Propose"
      note="The learner graduates only once the proposal is approved."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Learner" divided={false} columns={2}>
        <Field label="Graduating class" hint={criteria.length ? undefined : "Set up graduation criteria in Settings first."}>
          <select required value={criteriaId} onChange={(event) => { setCriteriaId(event.target.value); setStudent(""); }} className="input mt-1">
            <option value="" disabled>Choose a class</option>
            {criteria.map((item) => <option key={item.id} value={item.id}>{item.class_level_name} · {item.name}</option>)}
          </select>
        </Field>
        <Field as="div" label="Learner" hint={chosen ? `${inClass.length} learners in ${chosen.class_level_name}` : "Choose the class first."}>
          <SearchPick items={inClass} value={student} onChange={setStudent} label={(item) => item.full_name} detail={(item) => item.student_id} placeholder="Search by name or number" empty={chosen ? "No learner matches" : "Choose the class first"} required />
        </Field>
      </FormSection>

      <FormSection title="The year" description={measures.data ? `From the records: ${measures.data.attendance_days} register days, ${measures.data.results_counted} published results.${fromRecords ? "" : " Changed from the records."}` : "Filled from the registers and published results once a learner is chosen."} columns={2}>
        <Field label="Attendance %">
          <input required type="number" min={0} max={100} step="0.01" value={attendance} onChange={(event) => setAttendance(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Average mark %">
          <input required type="number" min={0} max={100} step="0.01" value={average} onChange={(event) => setAverage(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Community service hours">
          <input required type="number" min={0} value={hours} onChange={(event) => setHours(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <div className="space-y-2 pt-1">
          <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={clubs} onChange={(event) => setClubs(event.target.checked)} className="h-4 w-4 accent-primary-600" /> Took part in clubs or sport</label>
          <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={passed} onChange={(event) => setPassed(event.target.checked)} className="h-4 w-4 accent-primary-600" /> Passed the final examination</label>
        </div>
      </FormSection>
      <Checks checks={checks} />

      <FormSection title="Ceremony" columns={2}>
        <Field label="Graduation date" optional hint="Defaults to the day it is approved.">
          <input type="date" value={graduationDate} onChange={(event) => setGraduationDate(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Ceremony date" optional>
          <input type="date" value={ceremony} onChange={(event) => setCeremony(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Honours" optional className="sm:col-span-2">
          <input maxLength={100} value={honours} onChange={(event) => setHonours(event.target.value)} className="input mt-1" placeholder="e.g. Best in Mathematics" />
        </Field>
        <Field as="div" label="Notes" optional className="sm:col-span-2">
          <AIAssistField rows={2} value={notes} onChange={(next) => setNotes(next)} placeholder="Anything the person deciding should know" context={{ key: "students.lifecycle_note", params: { kind: "graduation_notes", student: Number(student) || null } }} draftLabel="Draft note" draftHint="Jot the facts first and it writes them up." />
        </Field>
      </FormSection>
    </FormModal>
  );
}
